// === RECHTSBIJSTAND SCREEN ===
// Rechtsbijstand (legal aid) insurance product screen
// Address search + family composition + coverage toggles

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User } from 'phosphor-react';
import {
  useWorkflow,
  useSharedAddress,
  useConfig,
  WorkflowPageLayout,
  bffClient,
  formatPrice,
  computeRechtsbijstandDisplayPremium,
} from '@sluitstraat/core';
import { commonStyles } from '../../styles/commonStyles';
import { stableStringify } from '../../utils/stableStringify';
import { shouldHideAddressSection, syncAddressToOtherProducts } from '../../utils/addressSharing';
import { readFamilyComposition, syncFamilyCompositionToOtherProducts } from '../../utils/familyComposition';
import AddressSection, { Address } from '../../components/AddressSection';
import { PersonalInfoSection } from '../../components/PersonalInfoSection';
import { DebugPanel } from '../../components/DebugPanel';
import { ToggleSwitch } from '../../components/ToggleSwitch';
import { CoverageOptionsCard } from '../../components/CoverageOptionsCard';
import { WithHelpSidebar } from '../../components/HelpCard';
import { WorkflowNavButtons } from '../../components/WorkflowNavButtons';
import { useWorkflowNav } from '../../hooks/useWorkflowNav';
import {
  RBV_COVERAGES,
  RBV_SECTION_COVERAGES,
  RBV_RP_CODE_TO_KEY,
  RentalUnitsFields,
  CoverageInfoLabel,
  useUpstreamSync,
  buildRechtsbijstandPayload,
  mergeRechtsbijstandResult,
  useCoverageCalculation,
  validateRechtsbijstand,
  scrollToFirstError,
} from './rechtsbijstand';

const SCREEN_ID = 'rechtsbijstand';

export const RechtsbijstandScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getStepData, saveStepData, selectedProducts } = useWorkflow();
  const { config } = useConfig();

  // Per-section ex-provision display value at FULL precision (not floored). The top badge
  // uses `computeRechtsbijstandDisplayPremium` (sum-precise, floor once at the end) so the
  // badge agrees with the summary / breakdown / email. Per-coverage "+€X" labels display
  // the precise value via `formatPrice`, which rounds half-to-even — that naturally averages
  // out so the labels sum to the same value as the top badge (no 5¢ arithmetic gap).
  const sectionDisplayPrice = (btp: number | string | undefined): number => {
    const n = Number(btp) || 0;
    if (n <= 0) return 0;
    const disc = (config as any)?.rechtsbijstandDiscount;
    const discActive = disc?.enabled && (disc.percentage ?? 0) > 0 && disc?.applyToData === false;
    const factor = discActive ? 1 - disc.percentage / 100 : 1;
    const pprc = 20;
    return n * factor * (1 - pprc / 100) * 1.21;
  };

  const [data, setData] = useState<any>(() => {
    const saved = getStepData(SCREEN_ID);
    return saved || {
      Contractdocument: {
        PP: {
          VP: {},
          VK: {},
          WC: { WC_CODE: '9901', WC_MYCODE: '9330', WC_GSS: '', WC_VZBFRNS: 0, WC_PPRC: 20, WC_ERB: 0, WC_EIGBVAK: 0, MP: [] },
          PP_BRANCHE: '092',
          PP_MYAAND: 'P020',
          PP_BETTERM: 1,
          PP_CDUUMND: 12,
          PP_BTWCD: 'I',
          PP_HANDACC: 'N',
          PP_PPRC: 20,
          PP_EKST: 0,
          VE: { VE_MYAAND: 'K781' },
          _rpEigendom: { RP_VWE: 1, RP_VBE: 0, RP_VGAVRH: 0 },
        },
      },
      Supportsection: {
        Dekkingen: {
          Consument_Wonen: 'J',
        },
      },
    };
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Apply an updater to the workflow data and persist the result. Centralises the
  // repeated "compute next state, then defer the save to outside the setState updater"
  // pattern (saving inside a React setState updater warns about a render-phase side effect).
  const persist = useCallback((updater: (prev: any) => any) => {
    setData((prev: any) => {
      const next = updater(prev);
      setTimeout(() => saveStepData(SCREEN_ID, next), 0);
      return next;
    });
  }, [saveStepData]);

  // Shared address hook (the canonical policyholder address; AddressSection owns lookup + sync)
  const { address: sharedAddress } = useSharedAddress();

  useEffect(() => {
    window.scrollTo(0, 0);
    sessionStorage.setItem('workflow_current_page', SCREEN_ID);
  }, []);

  useEffect(() => {
    if (!selectedProducts?.rechtsbijstand) {
      navigate('/workflow/product-selection', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only guard; re-running on selectedProducts/navigate identity would redirect mid-flow
  }, []);

  // Seed address (BA/VP) + Gezinssamenstelling from the shared address / upstream products.
  const { isGezinsamenstellingLocked, familyComposition, setFamilyComposition } = useUpstreamSync({
    data,
    sharedAddress,
    selectedProducts,
    getStepData,
    persist,
  });

  // Auto-calculation of coverage premiums (build payload, call BFF, merge; debounced + remount).
  const {
    isCalculating,
    calculationError,
    setCalculationError,
    lastCalculationInput,
    lastCalculationOutput,
    handleCalculateAll,
    previewPrices,
  } = useCoverageCalculation(data, persist);

  // Address (BA) edited via the shared AddressSection. Write BA + VP, persist, clear
  // validation, and push to the other selected products. AddressSection (shareAddress)
  // updates the shared slice itself; the auto-calc re-fires off the data.BA change.
  const handleAddressChange = useCallback((a: Address) => {
    persist((prev: any) => {
      const huisNr = Number(a.houseNumber);
      return {
        ...prev,
        Contractdocument: {
          ...prev?.Contractdocument,
          PP: {
            ...prev?.Contractdocument?.PP,
            BA: {
              ...prev?.Contractdocument?.PP?.BA,
              BA_PCODE: a.postcode,
              BA_HUISNR: a.houseNumber,
              BA_TOEVOEG: a.addition || '',
              BA_STRAAT: a.street || '',
              BA_PLAATS: a.city || '',
            },
            VP: {
              ...prev?.Contractdocument?.PP?.VP,
              VP_PCODE: a.postcode,
              VP_HUISNR: isNaN(huisNr) ? 0 : huisNr,
              VP_TOEVOEG: a.addition || '',
              VP_STRAAT: a.street || '',
              VP_PLAATS: a.city || '',
            },
          },
        },
      };
    });

    setValidationErrors(prev => {
      const u = { ...prev };
      delete u['BA_PCODE'];
      delete u['BA_HUISNR'];
      delete u['BA_STRAAT'];
      return u;
    });

    if (a.postcode && a.houseNumber) {
      syncAddressToOtherProducts(SCREEN_ID, {
        BA_PCODE: a.postcode,
        BA_HUISNR: a.houseNumber,
        BA_TOEVOEG: a.addition || '',
        BA_STRAAT: a.street || '',
        BA_PLAATS: a.city || '',
      }, selectedProducts, getStepData, saveStepData);
    }
  }, [persist, saveStepData, selectedProducts, getStepData])

  const { currentStep, totalSteps, productIndex, totalProducts, goToNext, goToPrevious } = useWorkflowNav(SCREEN_ID);

  const [isNextCalculating, setIsNextCalculating] = useState(false);

  const handleNext = async () => {
    const errors = validateRechtsbijstand(data, selectedProducts, getStepData, t);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      scrollToFirstError(errors);
      return;
    }

    try {
      setIsNextCalculating(true);

      // Final calculation with only selected coverages
      const payload = buildRechtsbijstandPayload(data, { selectedOnly: true });

      // Identical request already calculated and saved on a previous Next: reuse that stored
      // final result instead of re-calling the engine. Any change (own fields, coverages,
      // shared address/GSS) alters the payload and misses this check.
      const fingerprint = stableStringify(payload);
      if (fingerprint === sessionStorage.getItem('rechtsbijstand_final_payload')
        && getStepData(SCREEN_ID)?.completed) {
        goToNext();
        return;
      }

      const bffResult = await bffClient.request<any>(
        'POST',
        '/api/v1/productservice/calculate',
        payload
      );

      if (bffResult?.output) {
        const mergedData = mergeRechtsbijstandResult(bffResult.output, data);
        mergedData.completed = true;

        const finalDekkingen = mergedData.Supportsection?.Dekkingen || {};
        // VK_GEZSAMST is the shared family composition — survives deselecting Verkeer.
        const preservedGss = mergedData.Contractdocument?.PP?.VK?.VK_GEZSAMST;
        for (const cov of RBV_SECTION_COVERAGES) {
          if (finalDekkingen[cov.key] !== 'J') delete mergedData.Contractdocument?.PP?.[cov.priceField!];
        }
        if (finalDekkingen.Verkeer !== 'J' && preservedGss && mergedData.Contractdocument?.PP) {
          mergedData.Contractdocument.PP.VK = { VK_GEZSAMST: preservedGss };
        }
        if (Array.isArray(mergedData.Contractdocument?.PP?.RP)) {
          mergedData.Contractdocument.PP.RP = mergedData.Contractdocument.PP.RP.filter(
            (rp: any) => {
              const key = RBV_RP_CODE_TO_KEY[rp?.RP_CODE];
              return key && finalDekkingen[key] === 'J';
            }
          );
          if (mergedData.Contractdocument.PP.RP.length === 0) delete mergedData.Contractdocument.PP.RP;
        }

        saveStepData(SCREEN_ID, mergedData);
        sessionStorage.setItem('rechtsbijstand_final_payload', fingerprint);
      } else {
        // Fallback: save current data if BFF returned no output
        const fallbackData = { ...data, completed: true };
        saveStepData(SCREEN_ID, fallbackData);
      }

      goToNext();
    } catch (error: any) {
      console.error('[Rechtsbijstand] Final calculation error:', error);
      setCalculationError(error?.message || t('common.premiumCalculationError'));
    } finally {
      setIsNextCalculating(false);
    }
  };

  const handleBack = () => {
    saveStepData(SCREEN_ID, data);
    goToPrevious();
  };

  return (
    <WorkflowPageLayout currentStep={currentStep} totalSteps={totalSteps}>
      <main style={commonStyles.page}>
        <div style={commonStyles.container}>
          <WithHelpSidebar stackEarly>
            <header style={commonStyles.contentHeader}>
              <h1 style={commonStyles.title}>{t('screens.rechtsbijstand.title')}</h1>
            </header>

            <div style={commonStyles.formColumn}>

          {!shouldHideAddressSection('rechtsbijstand', selectedProducts, getStepData) && (
            <AddressSection
              icon={User}
              value={{
                postcode: data?.Contractdocument?.PP?.BA?.BA_PCODE || '',
                houseNumber: data?.Contractdocument?.PP?.BA?.BA_HUISNR != null ? String(data.Contractdocument.PP.BA.BA_HUISNR) : '',
                addition: data?.Contractdocument?.PP?.BA?.BA_TOEVOEG || '',
                street: data?.Contractdocument?.PP?.BA?.BA_STRAAT || '',
                city: data?.Contractdocument?.PP?.BA?.BA_PLAATS || '',
              }}
              onChange={handleAddressChange}
            />
          )}

          {!(isGezinsamenstellingLocked && shouldHideAddressSection('rechtsbijstand', selectedProducts, getStepData)) && (
            <PersonalInfoSection
              id="gezsamst"
              title={t('screens.rechtsbijstand.sections.personalDetails')}
              label={t('screens.rechtsbijstand.fields.familyComposition')}
              placeholder={t('screens.rechtsbijstand.placeholders.selectOption')}
              value={familyComposition || data?.Contractdocument?.PP?.VK?.VK_GEZSAMST || readFamilyComposition(data) || ''}
              disabled={isGezinsamenstellingLocked}
              error={validationErrors['VK_GEZSAMST'] || null}
              onChange={(value) => {
                if (isGezinsamenstellingLocked) return;
                if (value) {
                  setValidationErrors(prev => { const u = { ...prev }; delete u['VK_GEZSAMST']; return u; });
                }
                persist((prev: any) => ({
                  ...prev,
                  Contractdocument: {
                    ...prev.Contractdocument,
                    PP: {
                      ...prev.Contractdocument?.PP,
                      VK: {
                        ...prev.Contractdocument?.PP?.VK,
                        VK_GEZSAMST: value,
                      },
                    },
                  },
                }));
                // Write to the single shared store (source of truth) + mirror to other products.
                if (value) {
                  setFamilyComposition(value);
                  syncFamilyCompositionToOtherProducts(SCREEN_ID, value, selectedProducts, getStepData, saveStepData);
                }
              }}
            />
          )}

          <CoverageOptionsCard
            title={t('screens.rechtsbijstand.sections.coverages')}
            totalLabel={t('screens.rechtsbijstand.sections.total')}
            total={computeRechtsbijstandDisplayPremium(data, config as any, data?.Supportsection?.Dekkingen || {}) || null}
            calculating={isCalculating}
            icon="shield-check"
            items={[
              {
                label: t('screens.rechtsbijstand.coverageOptions.consumentWonen'),
                descriptions: [1, 2, 3, 4].map((n) =>
                  t(`screens.rechtsbijstand.coverageInfo.consumentWonen.item${n}`)
                ),
                amount: sectionDisplayPrice(data?.Contractdocument?.PP?.WC?.WC_BTP) || null,
              },
            ]}
          >
            <div style={commonStyles.coverageDivider} />

            <div className="rechtsbijstand-optional-grid">
              {RBV_COVERAGES.map((coverage) => {
                const dekkingen = data?.Supportsection?.Dekkingen || {};
                const isEnabled = dekkingen[coverage.key] === 'J';
                // Per-coverage ex-provision display (BTP × 0.8 × 1.21 → floor) so the
                // "+€X" badges sum to the top coverage total badge.
                // Step data only carries SELECTED coverages after the final calc; unselected
                // previews fall back to the cached all-options pricing (display-only, never
                // merged into the data that reaches the calculation/package endpoints). The
                // fallback works per price field, not per section: an unselected Verkeer keeps
                // a VK stub carrying the shared VK_GEZSAMST, so the section exists without a
                // VK_BTP and a section-level fallback would never fire.
                let premium = 0;
                if (coverage.priceField) {
                  const btpKey = `${coverage.priceField}_BTP`;
                  const btp = data?.Contractdocument?.PP?.[coverage.priceField]?.[btpKey]
                    ?? previewPrices?.sections?.[coverage.priceField]?.[btpKey];
                  premium = sectionDisplayPrice(btp);
                } else if (coverage.code) {
                  const rpArray = Array.isArray(data?.Contractdocument?.PP?.RP) ? data.Contractdocument.PP.RP : [];
                  const rpItem = rpArray.find((rp: any) => rp?.RP_CODE === coverage.code)
                    ?? previewPrices?.rp?.find((rp: any) => rp?.RP_CODE === coverage.code);
                  premium = sectionDisplayPrice(rpItem?.RP_BTP);
                }
                return (
                  <div
                    key={coverage.key}
                    style={{ ...commonStyles.coverageOptionsRow, cursor: 'pointer' }}
                    onClick={() => {
                      persist((prev: any) => ({
                        ...prev,
                        Supportsection: {
                          ...prev.Supportsection,
                          Dekkingen: {
                            ...prev.Supportsection?.Dekkingen,
                            [coverage.key]: isEnabled ? 'N' : 'J',
                          },
                        },
                      }));
                    }}
                  >
                    <div style={commonStyles.coverageToggleContent}>
                      <label style={{ ...commonStyles.coverageOptionsRowLabel, ...commonStyles.minWidth0, cursor: 'pointer' }}>
                        <CoverageInfoLabel label={t(coverage.labelKey)} infoKey={coverage.infoKey} />
                      </label>
                      {/* Fixed group: price + toggle never shrink, so a long wrapping label keeps
                          them inside the card on the right. */}
                      <div style={commonStyles.coverageRowEndGroup}>
                        {premium > 0 && (
                          <span style={{
                            ...commonStyles.coveragePriceBadgeSmall,
                            ...commonStyles.mr12,
                            ...(!isEnabled && commonStyles.coveragePriceBadgeDisabled),
                          }}>
                            + &euro;{formatPrice(premium)}
                          </span>
                        )}
                        <ToggleSwitch checked={isEnabled} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Rental units - shown when Eigendom & Verhuur is enabled */}
            {data?.Supportsection?.Dekkingen?.Eigendom_Verhuur_Onroerende_Zaken === 'J' && (
              <RentalUnitsFields data={data} setData={setData} />
            )}

            {calculationError && (
              <div style={commonStyles.inlineErrorBanner}>
                {calculationError}
              </div>
            )}
          </CoverageOptionsCard>

            </div>

            <WorkflowNavButtons
              onBack={handleBack}
              onNext={handleNext}
              nextLabel={isNextCalculating ? 'screens.rechtsbijstand.calculating' : 'common.nextPlain'}
              nextLoading={isNextCalculating}
              center={
                <div style={commonStyles.productCounter}>
                  {t('common.productCounter', { current: productIndex, total: totalProducts })}
                </div>
              }
            />
          </WithHelpSidebar>

          <DebugPanel
            input={lastCalculationInput}
            output={lastCalculationOutput}
            onRecalculate={handleCalculateAll}
            calculating={isCalculating}
            validationErrors={validationErrors}
          />

        </div>
      </main>
    </WorkflowPageLayout>
  );
};

export default RechtsbijstandScreen;
